import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertTriangle, Loader2, Lock, Save, X } from "lucide-react";
import { useState } from "react";
import { OrgAdminApiError } from "../api/org-admin-client.js";
import { useOrgAdminClient } from "../hooks/useOrgAdminClient.js";
import { useOrgAdminT } from "../i18n/context.js";
import { effectiveInvitationStatus } from "../types/org-admin.js";
/**
 * Whether this user's email may be edited at all.
 *
 * `=== "sent"` and nothing else. `effectiveInvitationStatus` reports
 * `undefined` for a row with no invitation state and no bound `clerkUserId`,
 * and that INDETERMINATE case must fall to locked: a row whose Clerk side
 * cannot be reasoned about is the last one to mutate blindly. lexserver 409s
 * on it too, so the two gates agree by construction rather than by mirroring.
 */
export function canEditEmail(user) {
    return effectiveInvitationStatus(user) === "sent";
}
/** Which "why not" to show beside a locked email field. */
export function emailLockReason(user) {
    return effectiveInvitationStatus(user) === "accepted" ? "accepted" : "indeterminate";
}
/**
 * What actually happened to an email change, judged from the RESPONSE rather
 * than the HTTP status.
 *
 * - `not-applied` — the server answered OK but returned a different address.
 *   A build without email support strips the field silently, so trusting the
 *   200 would tell the operator an address changed when it did not, and they
 *   would only discover it when the invitation reached the wrong inbox.
 * - `delivery-failed` — applied, but `emailSent === false`: minted and never
 *   delivered. Strictly `=== false`; an ABSENT `emailSent` means "not
 *   applicable to this call" and must not be read as failure, or every
 *   name-only edit would cry wolf.
 */
export function classifyEmailSave(input) {
    if (input.returnedEmail !== input.requestedEmail)
        return "not-applied";
    return input.emailSent === false ? "delivery-failed" : "applied";
}
/**
 * Did the server MUTATE the row even though it returned an error?
 *
 * `INVITATION_REISSUE_FAILED` (502) is the one error where the answer is yes:
 * lexserver writes the new address first, then re-issues the Clerk invitation
 * (write-then-revoke, so a duplicate address costs zero Clerk mutations). If
 * the re-issue throws, the address IS saved and no invitation exists.
 *
 * Two consequences the UI must not get wrong, both non-obvious:
 *  1. the table behind the dialog is now STALE — it still shows the old
 *     address — so the host has to reload despite the failure;
 *  2. **retrying the form cannot fix it.** The server compares the submitted
 *     email against the stored one, which now matches, so a second submit
 *     returns a cheerful 200 and re-issues nothing. Resend is the only
 *     recovery, which is why the server's message says so and we render it
 *     verbatim rather than substituting our own.
 */
export function serverMutatedDespiteError(code) {
    return code === "INVITATION_REISSUE_FAILED";
}
/**
 * Edit an existing user. Sibling of [InviteUserDialog](./InviteUserDialog.tsx)
 * and deliberately the same inline-panel shape.
 *
 * **The email rule.** A user's email is their Clerk sign-in credential, so it
 * is editable ONLY while the invitation is still pending (`sent`) — at that
 * point nobody has signed in with it yet and the server can revoke the
 * outstanding invitation and re-issue to the new address atomically. Otherwise
 * the field goes read-only with the reason shown, because changing Mongo alone
 * would leave the person's actual login on the old address: the UI would show
 * one thing and Clerk would enforce another. That is a lie the operator only
 * discovers at the next sign-in, so the gate is here rather than a warning.
 *
 * Note the gate is `=== "sent"`, so an INDETERMINATE row (no invitation state
 * and no bound `clerkUserId`, which `effectiveInvitationStatus` reports as
 * `undefined`) falls to locked without a special case — matching lexserver's
 * rule, which also 409s on unknown. Unknown is not permission: a row whose
 * Clerk side cannot be reasoned about is the last one to mutate blindly.
 *
 * `role` is deliberately NOT editable here — see the note on the render
 * below.
 */
export function EditUserDialog({ user, onClose, onSaved, onDataChanged }) {
    const t = useOrgAdminT();
    const client = useOrgAdminClient();
    const [displayName, setDisplayName] = useState(user.displayName ?? "");
    const [email, setEmail] = useState(user.email);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Pending invitation ⇒ nobody has signed in with this address yet.
    const emailEditable = canEditEmail(user);
    const trimmedName = displayName.trim();
    const trimmedEmail = email.trim();
    const nameChanged = trimmedName !== (user.displayName ?? "");
    const emailChanged = emailEditable && trimmedEmail !== user.email;
    const dirty = nameChanged || emailChanged;
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!dirty || saving)
            return;
        setSaving(true);
        setError(null);
        // Diff-only patch (the ClientForm precedent): sending an unchanged email
        // would make the server run the revoke/re-issue dance for nothing.
        const patch = {};
        if (nameChanged)
            patch.displayName = trimmedName;
        if (emailChanged)
            patch.email = trimmedEmail;
        try {
            const res = await client.updateUser(user.userId, patch);
            // The response is the authority, not the 200 — see classifyEmailSave.
            const outcome = emailChanged
                ? classifyEmailSave({
                    requestedEmail: trimmedEmail,
                    returnedEmail: res.data.email,
                    emailSent: res.data.emailSent,
                })
                : "applied";
            if (outcome === "not-applied") {
                setError(t("editUser.emailNotApplied"));
                return;
            }
            // Absent means no URL was produced — never "". Normalise an empty
            // string away here so the host cannot render a blank panel.
            const url = res.data.invitationUrl?.trim();
            onSaved({
                invitationUrl: url || undefined,
                email: res.data.email,
                emailDeliveryFailed: outcome === "delivery-failed",
            });
        }
        catch (err) {
            if (err instanceof OrgAdminApiError) {
                // Server-side rule, server-side wording: this code covers BOTH
                // "already accepted" and "invitation state unknown", and which one
                // it is lives in lexserver. Rendering its message beats mirroring a
                // distinction here that no test of ours could hold to its source.
                if (err.code === "EMAIL_IMMUTABLE_AFTER_ACCEPT") {
                    setError(err.message?.trim() || t("editUser.errNotEditable"));
                    return;
                }
                if (err.code === "DUPLICATE_EMAIL") {
                    setError(t("editUser.errDuplicate", { email: trimmedEmail }));
                    return;
                }
                // Address SAVED, invitation NOT re-issued. Verbatim server message —
                // it names Resend as the only recovery, and re-submitting this form
                // would silently succeed while sending nothing. The row changed, so
                // the host must reload even though this is an error path.
                if (serverMutatedDespiteError(err.code)) {
                    setError(err.message?.trim() || t("editUser.errReissueFailed"));
                    onDataChanged?.();
                    return;
                }
            }
            setError(err instanceof Error ? err.message : t("editUser.failedSave"));
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsxs("div", { className: "mb-6 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900", children: [_jsxs("div", { className: "mb-3 flex items-center justify-between", children: [_jsx("h2", { className: "text-sm font-semibold", children: t("editUser.title", { name: user.displayName }) }), _jsx("button", { type: "button", onClick: onClose, className: "text-slate-400 hover:text-slate-600", children: _jsx(X, { className: "h-4 w-4" }) })] }), error && (_jsxs("div", { className: "mb-3 flex items-start gap-1.5 rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400", children: [_jsx(AlertTriangle, { className: "mt-0.5 h-3.5 w-3.5 shrink-0" }), _jsx("span", { children: error })] })), _jsxs("form", { onSubmit: handleSubmit, className: "flex flex-wrap items-start gap-3", children: [_jsxs("label", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: t("common.name") }), _jsx("input", { value: displayName, onChange: (e) => setDisplayName(e.target.value), required: true, "data-testid": "edit-user-display-name", className: "w-52 rounded-md border border-slate-200 bg-transparent px-3 py-1.5 text-sm outline-none focus:border-indigo-400 dark:border-slate-700" })] }), _jsxs("label", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: t("common.email") }), _jsx("input", { value: email, onChange: (e) => setEmail(e.target.value), type: "email", required: true, disabled: !emailEditable, "data-testid": "edit-user-email", title: emailEditable ? undefined : t("editUser.emailLockedTitle"), className: "w-72 rounded-md border border-slate-200 bg-transparent px-3 py-1.5 text-sm outline-none focus:border-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700" }), emailEditable ? (_jsx("span", { className: "max-w-72 text-xs text-amber-600 dark:text-amber-500", children: t("editUser.emailReissueHint") })) : (_jsxs("span", { className: "flex max-w-72 items-start gap-1 text-xs text-slate-400", children: [_jsx(Lock, { className: "mt-0.5 h-3 w-3 shrink-0" }), emailLockReason(user) === "accepted"
                                        ? t("editUser.emailLockedAccepted")
                                        : t("editUser.emailLockedUnknown")] }))] }), _jsxs("button", { type: "submit", disabled: saving || !dirty, "data-testid": "edit-user-save", className: "mt-5 inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50", children: [saving ? _jsx(Loader2, { className: "h-3.5 w-3.5 animate-spin" }) : _jsx(Save, { className: "h-3.5 w-3.5" }), t("common.save")] })] })] }));
}
//# sourceMappingURL=EditUserDialog.js.map